import React, { Suspense, lazy } from 'react';

import {
  BrowserRouter,
  Routes,
  Route,
  useLocation
} from 'react-router-dom';

// =====================
// Shared Public Components (Keep these static for immediate load)
// =====================
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToAnchor from './components/ScrollToAnchor';
import ScrollToTop from './components/ScrollToTop';
import FloatingWhatsApp from './pages/FloatingWhatsApp';
import BackToTopButton from './components/BackToTopButton';
import ProtectedRoute from './admin/components/ProtectedRoute';
import AdminLayout from './admin/components/AdminLayout';

// =====================
// LAZY LOADED PUBLIC PAGES
// =====================
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const CampusPage = lazy(() => import('./pages/CampusPage'));
const LifeAtJNJPage = lazy(() => import('./pages/LifeAtJNJPage'));
const CurricularPage = lazy(() => import('./pages/CurricularPage'));
const CoCurricularPage = lazy(() => import('./pages/CoCurricularPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const AlumniPage = lazy(() => import('./pages/AlumniPage'));
const BoardResultPage = lazy(() => import('./pages/BoardResultPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const JoinUsPage = lazy(() => import('./pages/JoinUsPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const SingleBlogPage = lazy(() => import('./pages/SingleBlogPage'));
const AdmissionInquiryPage = lazy(() => import('./pages/AdmissionInquiryPage'));

// NEW: Dedicated Ad Landing Page
const VicePrincipalLandingPage = lazy(() => import('./pages/VicePrincipalLandingPage'));

// =====================
// LAZY LOADED ADMIN PAGES
// =====================
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const Dashboard = lazy(() => import('./admin/pages/Dashboard'));
const ManageAnnouncements = lazy(() => import('./admin/pages/ManageAnnouncements'));
const ManageLeadership = lazy(() => import('./admin/pages/ManageLeadership'));
const ManageTestimonials = lazy(() => import('./admin/pages/ManageTestimonials'));
const ManageHeroSlides = lazy(() => import('./admin/pages/ManageHeroSlides'));
const ManageGallery = lazy(() => import('./admin/pages/ManageGallery'));
const ManageBookLists = lazy(() => import('./admin/pages/ManageBookLists'));
const ManageSiteContent = lazy(() => import('./admin/pages/ManageSiteContent'));
const ManageSiteImages = lazy(() => import('./admin/pages/ManageSiteImages'));
const ManageFacilities = lazy(() => import('./admin/pages/ManageFacilities'));
const ManageFaculty = lazy(() => import('./admin/pages/ManageFaculty'));
const ManageBoardResults = lazy(() => import('./admin/pages/ManageBoardResults'));
const ManageAlumni = lazy(() => import('./admin/pages/ManageAlumni'));
const ManageCareers = lazy(() => import('./admin/pages/ManageCareers'));
const ManageContact = lazy(() => import('./admin/pages/ManageContact'));
const ManageBlogs = lazy(() => import('./admin/pages/ManageBlogs'));
const ManageAdmissions = lazy(() => import('./admin/pages/ManageAdmissions'));
const ManageStudents = lazy(() => import('./admin/pages/ManageStudents'));
const ManageBanners = lazy(() => import('./admin/pages/ManageBanners'));
const ManageFeedback = lazy(() => import('./admin/pages/ManageFeedback'));
const ManageStudentArt = lazy(() => import('./admin/pages/ManageStudentArt'));

// =====================
// Loading Spinner Component
// =====================
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-screen w-full bg-gray-50">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-orange-500"></div>
  </div>
);

// =====================
// Layout Component
// =====================
const Layout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Check if current route is the ad landing page
  const isLandingRoute = location.pathname === '/hiring/vice-principal';

  // =====================
  // Admin Routing
  // =====================
  if (isAdminRoute) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Admin Route */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Protected Admin Routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin/dashboard" element={<Dashboard />} />
              <Route path="/admin/manage-leadership" element={<ManageLeadership />} />
              <Route path="/admin/manage-testimonials" element={<ManageTestimonials />} />
              <Route path="/admin/manage-hero-slides" element={<ManageHeroSlides />} />
              <Route path="/admin/manage-gallery" element={<ManageGallery />} />
              <Route path="/admin/manage-book-lists" element={<ManageBookLists />} />
              <Route path="/admin/manage-site-content" element={<ManageSiteContent />} />
              <Route path="/admin/manage-site-images" element={<ManageSiteImages />} />
              <Route path="/admin/manage-campus-facilities" element={<ManageFacilities />} />
              <Route path="/admin/manage-faculty" element={<ManageFaculty />} />
              <Route path="/admin/manage-board-results" element={<ManageBoardResults />} />
              <Route path="/admin/manage-alumni" element={<ManageAlumni />} />
              <Route path="/admin/manage-announcements" element={<ManageAnnouncements />} />
              <Route path="/admin/manage-careers" element={<ManageCareers />} />
              <Route path="/admin/manage-contact" element={<ManageContact />} />
              <Route path="/admin/manage-blogs" element={<ManageBlogs />} />
              <Route path="/admin/manage-admissions" element={<ManageAdmissions />} />
              <Route path="/admin/manage-students" element={<ManageStudents />} />
              <Route path="/admin/manage-banners" element={<ManageBanners />} />
              <Route path="/admin/manage-feedback" element={<ManageFeedback />} />
              <Route path="/admin/manage-student-art" element={<ManageStudentArt />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    );
  }

  // =====================
  // Public Website Routing
  // =====================
  return (
    <>
      <ScrollToAnchor />
      <ScrollToTop />

      {/* Hide Navbar on Ad Landing Page */}
      {!isLandingRoute && <Navbar />}

      <main className="min-h-screen">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/campus" element={<CampusPage />} />
            <Route path="/life-at-jnj" element={<LifeAtJNJPage />} />
            <Route path="/curricular" element={<CurricularPage />} />
            <Route path="/co-curricular" element={<CoCurricularPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/board-results" element={<BoardResultPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/alumni" element={<AlumniPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/join-us" element={<JoinUsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<SingleBlogPage />} />
            <Route path="/admission-inquiry" element={<AdmissionInquiryPage />} />
            
            {/* Ad Landing Page Route */}
            <Route path="/hiring/vice-principal" element={<VicePrincipalLandingPage />} />
          </Routes>
        </Suspense>
      </main>

      {/* Hide Footer & Widgets on Ad Landing Page */}
      {!isLandingRoute && <Footer />}
      {!isLandingRoute && <BackToTopButton />} 
      {!isLandingRoute && <FloatingWhatsApp />}
    </>
  );
};

// =====================
// App Root
// =====================
function App() {
  return (
    <BrowserRouter>
      <Layout />
    </BrowserRouter>
  );
}

export default App;